"use client";

import { useRouter } from "next/navigation";
import { useEffect } from "react";
import { checkVerified, refreshVerification } from "../actions";
import { AUTH_TTL_SECONDS } from "../constants";
import { sessionExpiredHref } from "../lib/session-expired";

/** Matches AUTH_TTL_SECONDS — the session cookie is dead by this point. */
const HARD_REFRESH_MS = AUTH_TTL_SECONDS * 1000;
const POLL_INTERVAL_MS = 60 * 1000;
/**
 * Minimum gap between activity-driven session refreshes. Each refresh sets
 * the cookie, which re-renders the route (and its SAP lookups) on the
 * server, so an idle timeout lands 9–10 minutes after the last activity.
 */
const REFRESH_THROTTLE_MS = 60 * 1000;
const ACTIVITY_EVENTS = ["pointerdown", "keydown"] as const;

/**
 * When the session was last refreshed. Module-level so the throttle holds
 * across watcher remounts on in-app navigation (dashboard ↔ order); 0 lets
 * the first activity of a page load refresh right away.
 */
let lastRefreshAt = 0;

/**
 * Keeps an active customer's session alive and sends an expired one back to
 * the preauth gate with the timed-out dialog (`/appointment?expired=1`).
 * Mounted on all authenticated views; navigating (rather than refreshing)
 * matters on the notification-link order page, where a plain re-render
 * would just re-mint the session and keep the page alive.
 *
 * Activity — a click/tap or keypress anywhere — re-issues the session
 * cookie with a fresh 10-minute expiry (`refreshVerification`, at most once
 * per REFRESH_THROTTLE_MS), so the timeout counts from the last activity.
 * Expiry is caught by:
 *
 * - a hard 10-minute timer, restarted on every refresh,
 * - a poll every minute that asks the server whether the cookie is still valid,
 * - the same check on tab/window refocus, so a backgrounded tab moves
 *   immediately instead of waiting out the poll.
 */
export default function SessionWatcher() {
  const router = useRouter();

  useEffect(() => {
    let cancelled = false;
    let hardTimer: ReturnType<typeof setTimeout> | undefined;

    const expire = () => {
      if (!cancelled) router.replace(sessionExpiredHref());
    };

    const checkAuth = async (assumeOnError: boolean) => {
      const authed = await checkVerified().catch(() => assumeOnError);
      if (!authed) expire();
    };

    // Poll/refocus checks assume still-authed on transport errors — never
    // kick a user out over a network blip. The hard timer is the backstop:
    // the cookie is definitively dead by then (unless refreshed in another
    // tab, which the check catches), so an error reads as expired.
    const startHardTimer = () => {
      clearTimeout(hardTimer);
      hardTimer = setTimeout(() => void checkAuth(false), HARD_REFRESH_MS);
    };

    const handleActivity = () => {
      const now = Date.now();
      if (now - lastRefreshAt < REFRESH_THROTTLE_MS) return;
      lastRefreshAt = now;
      refreshVerification().then(
        (alive) => {
          if (cancelled) return;
          if (alive) startHardTimer();
          else expire();
        },
        // A failed refresh leaves the session as it was; the poll decides.
        () => undefined,
      );
    };
    const handleVisibility = () => {
      if (document.visibilityState === "visible") void checkAuth(true);
    };
    const handleFocus = () => void checkAuth(true);

    startHardTimer();
    const poll = setInterval(() => void checkAuth(true), POLL_INTERVAL_MS);
    // Capture phase, so activity inside components that stop propagation
    // (dialogs, menus) still counts.
    for (const type of ACTIVITY_EVENTS) {
      document.addEventListener(type, handleActivity, { capture: true });
    }
    document.addEventListener("visibilitychange", handleVisibility);
    window.addEventListener("focus", handleFocus);

    return () => {
      cancelled = true;
      clearTimeout(hardTimer);
      clearInterval(poll);
      for (const type of ACTIVITY_EVENTS) {
        document.removeEventListener(type, handleActivity, { capture: true });
      }
      document.removeEventListener("visibilitychange", handleVisibility);
      window.removeEventListener("focus", handleFocus);
    };
  }, [router]);

  return null;
}
